// `release-notes.json`, fetched once per `ApiClient` and shared by every reader in the tab.
//
// Two readers need the list: the account menu’s dot needs the newest id before anyone
// opens “What’s new”, and `WhatsNew.tsx` needs the whole thing once they do. Caching the
// promise rather than the resolved value means a second reader that mounts before the
// first request settles awaits the one already in flight instead of sending a second.
//
// Keyed by the client instance, not a bare module-level variable: `App.tsx` builds exactly
// one for as long as the tab is open, so in practice this is "once, ever" — but keying it
// means a second `ApiClient` (a different tab in the same test run, one test's fixture
// after another's) gets its own fetch instead of the first one's cached answer forever.

import type { ApiClient } from './api/client.js';
import type { ReleaseNote } from './api/contract.js';

const cache = new WeakMap<ApiClient, Promise<readonly ReleaseNote[]>>();

export function releaseNotes(client: ApiClient): Promise<readonly ReleaseNote[]> {
  const cached = cache.get(client);
  if (cached !== undefined) {
    return cached;
  }
  const request = client.releaseNotes().catch((error: unknown) => {
    // Not cached on failure: a retry, from the same reader or the next one, tries again
    // instead of replaying the same failure forever.
    cache.delete(client);
    throw error;
  });
  cache.set(client, request);
  return request;
}
